import React, {
  useState,
  useCallback,
  useMemo,
  useRef,
  useEffect,
} from "react";
import Data from "../../../apiData/yoga2.json";
import useAsanaSearch, { ALL_TAGS } from "./useAsanaSearch";
import useRogyogAnalytics from "./useRogyogAnalytics";
import { RogyogSeoMeta } from "./RogyogSeoMeta";
import {
  Section,
  Inner,
  Breadcrumb,
  BreadcrumbLink,
  BreadcrumbCurrent,
  BreadcrumbSep,
  HeroWrap,
  Eyebrow,
  Title,
  Desc,
  BadgeRow,
  BadgeCard,
  BadgeIconWrap,
  BadgeLabel,
  CategoryPillsRow,
  CategoryPill,
  SearchFilterRow,
  SearchBox,
  SearchInput,
  FilterToggleButton,
  FilterPanel,
  FilterPanelTitle,
  TagSuggestions,
  TagChip,
  NoResultsBox,
  LayoutGrid,
  Sidebar,
  SideCard,
  SideCardHeader,
  SideList,
  SideItem,
  SideItemLabel,
  SideBadge,
  ViewAllButton,
  WatchlistCard,
  WatchlistIconWrap,
  WatchlistText,
  WatchlistTitle,
  WatchlistSubtitle,
  WatchlistChevron,
  MainCol,
  PlayerCard,
  PlayerFrame,
  PlayerThumbButton,
  PlayerThumbPlayCircle,
  PlayerBody,
  PlayerTitleRow,
  PlayerTitle,
  PlayerDuration,
  ConditionTagsRow,
  ConditionTag,
  EmptyPlayerState,
  DetailBox,
  AccordionBtn,
  AccordionIconWrap,
  AccordionBody,
  AccordionInner,
  MoreSection,
  MoreHeader,
  MoreTitle,
  ViewAllLink,
  MoreTrackWrap,
  MoreGrid,
  MoreCard,
  MoreThumb,
  MoreThumbDuration,
  MoreBookmark,
  MoreCardBody,
  MoreCardTitle,
  MoreCardSubtitle,
  CarouselNavButton,
  CTABanner,
  CTALeft,
  CTAIconWrap,
  CTATitle,
  CTASubtitle,
  CTAButton,
} from "./Rogyog.styles";

// ─── Constants ─────────────────────────────────────────────────────────────────

const YT_EMBED = "https://www.youtube.com/embed";
const ytThumb = (id) => `https://img.youtube.com/vi/${id}/mqdefault.jpg`;
const splitLines = (str = "") =>
  str
    .split("&&")
    .map((s) => s.trim())
    .filter(Boolean);

const MORE_PAGE_SIZE = 4;
const WATCHLIST_STORAGE_KEY = "yogashastra:rogyog:watchlist";

// ─── Icon atoms ────────────────────────────────────────────────────────────────
// Kept as small single-path/stroke SVGs, same weight as the app's other
// inline icon atoms (see e.g. YogGoalAsanas.js's hero icons) rather than
// pulling in an icon library for a handful of glyphs.

const PlayIcon = (props) => (
  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" {...props}>
    <path d="M8 5v14l11-7z" />
  </svg>
);

const SearchIcon = (props) => (
  <svg
    className="search-icon"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    width="16"
    height="16"
    {...props}
  >
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const FilterIcon = (props) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    width="15"
    height="15"
    {...props}
  >
    <polygon points="4 4 20 4 14 12.5 14 19 10 21 10 12.5 4 4" />
  </svg>
);

const HomeIcon = (props) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    width="14"
    height="14"
    {...props}
  >
    <path d="M3 11l9-8 9 8" />
    <path d="M5 10v10h14V10" />
  </svg>
);

const LeafIcon = (props) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    width="16"
    height="16"
    {...props}
  >
    <path d="M5 21c8 0 14-6 14-16C9 5 5 11 5 21z" />
    <path d="M5 21c0-5 3-9 8-11" />
  </svg>
);

const PersonIcon = (props) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    width="16"
    height="16"
    {...props}
  >
    <circle cx="12" cy="8" r="4" />
    <path d="M4 21c0-4.4 3.6-8 8-8s8 3.6 8 8" />
  </svg>
);

const HeartIcon = ({ filled, ...props }) => (
  <svg
    viewBox="0 0 24 24"
    fill={filled ? "currentColor" : "none"}
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    width="16"
    height="16"
    {...props}
  >
    <path d="M20.8 8.6c0 4.9-8.8 10.4-8.8 10.4S3.2 13.5 3.2 8.6a4.6 4.6 0 0 1 8.8-1.9 4.6 4.6 0 0 1 8.8 1.9z" />
  </svg>
);

const ShieldIcon = (props) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    width="16"
    height="16"
    {...props}
  >
    <path d="M12 3l8 3v6c0 5-3.4 8.4-8 9-4.6-.6-8-4-8-9V6z" />
  </svg>
);

const PeopleIcon = (props) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    width="16"
    height="16"
    {...props}
  >
    <circle cx="9" cy="8" r="3.2" />
    <path d="M2.5 20c0-3.6 2.9-6.4 6.5-6.4S15.5 16.4 15.5 20" />
    <circle cx="17" cy="9" r="2.6" />
    <path d="M15 13.4c2.6.4 4.5 2.6 4.5 5.3" />
  </svg>
);

const ClipboardIcon = (props) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    width="16"
    height="16"
    {...props}
  >
    <rect x="6" y="4" width="12" height="17" rx="2" />
    <path d="M9 4V3a1 1 0 011-1h4a1 1 0 011 1v1" />
    <path d="M9 11h6M9 15h6" />
  </svg>
);

const SparkleIcon = (props) => (
  <svg
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
    width="16"
    height="16"
    {...props}
  >
    <path d="M12 2l2.2 6.8L21 11l-6.8 2.2L12 20l-2.2-6.8L3 11l6.8-2.2z" />
  </svg>
);

const ClockIcon = (props) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    width="12"
    height="12"
    {...props}
  >
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 3" />
  </svg>
);

const ChevronRightIcon = (props) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    width="15"
    height="15"
    {...props}
  >
    <polyline points="9 6 15 12 9 18" />
  </svg>
);

const ArrowRightIcon = (props) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.4"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    width="15"
    height="15"
    {...props}
  >
    <line x1="4" y1="12" x2="20" y2="12" />
    <polyline points="14 6 20 12 14 18" />
  </svg>
);

const CATEGORY_PILL_ICONS = [PersonIcon, ShieldIcon, PeopleIcon];
const CATEGORY_PILL_COLORS = ["#3d6b4f", "#c9822f", "#7c5cbf"];

// ─── Watchlist (localStorage-backed, self-contained) ──────────────────────────
// No shared "useLocalStorageSet"-style hook is available to this file, so
// this is a small inline equivalent — same idea (a Set of ids persisted to
// localStorage), scoped to Rogyog only via WATCHLIST_STORAGE_KEY.

function useWatchlist() {
  const [ids, setIds] = useState(() => {
    try {
      const raw = window.localStorage.getItem(WATCHLIST_STORAGE_KEY);
      return raw ? new Set(JSON.parse(raw)) : new Set();
    } catch {
      return new Set();
    }
  });

  const toggle = useCallback((id) => {
    setIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      try {
        window.localStorage.setItem(WATCHLIST_STORAGE_KEY, JSON.stringify([...next]));
      } catch {
        // Storage unavailable (private browsing, quota, etc.) — watchlist
        // just won't persist across reloads; not worth surfacing an error.
      }
      return next;
    });
  }, []);

  return [ids, toggle];
}

// ─── Accordion ─────────────────────────────────────────────────────────────────

const ACCORDION_META = {
  "About this Asana": { Icon: LeafIcon, bg: "rgba(61,107,79,0.12)", color: "#3d6b4f" },
  Instructions: { Icon: ClipboardIcon, bg: "rgba(201,130,47,0.14)", color: "#c9822f" },
  Benefits: { Icon: SparkleIcon, bg: "rgba(124,92,191,0.14)", color: "#7c5cbf" },
  "Precautions & Consequences": { Icon: ShieldIcon, bg: "rgba(61,124,191,0.14)", color: "#3d7cbf" },
};

const Accordion = ({ label, defaultOpen = false, children, onToggle }) => {
  const [open, setOpen] = useState(defaultOpen);
  const id = `acc-${label.replace(/\s+/g, "-").toLowerCase()}`;
  const meta = ACCORDION_META[label] || { Icon: LeafIcon, bg: undefined, color: undefined };
  const Icon = meta.Icon;

  const handleToggle = useCallback(() => {
    setOpen((p) => {
      const next = !p;
      onToggle?.(label, next);
      return next;
    });
  }, [label, onToggle]);

  return (
    <>
      <AccordionBtn $open={open} onClick={handleToggle} aria-expanded={open} aria-controls={id}>
        <AccordionIconWrap $bg={meta.bg} $color={meta.color}>
          <Icon />
        </AccordionIconWrap>
        {label}
        <span className="chevron" aria-hidden="true">
          <ChevronRightIcon style={{ transform: "rotate(90deg)" }} />
        </span>
      </AccordionBtn>
      <AccordionBody $open={open} id={id} role="region">
        <AccordionInner $open={open}>{children}</AccordionInner>
      </AccordionBody>
    </>
  );
};

// ─── AsanaDetail ───────────────────────────────────────────────────────────────

const AsanaDetail = ({ asana, onAccordionToggle }) => {
  if (!asana) return null;
  return (
    <DetailBox>
      {asana.description && (
        <Accordion label="About this Asana" defaultOpen onToggle={onAccordionToggle}>
          <p>{asana.description}</p>
        </Accordion>
      )}
      {asana.instruction && (
        <Accordion label="Instructions" onToggle={onAccordionToggle}>
          <ol>
            {splitLines(asana.instruction).map((l, i) => (
              <li key={i}>{l}</li>
            ))}
          </ol>
        </Accordion>
      )}
      {asana.benifits && (
        <Accordion label="Benefits" onToggle={onAccordionToggle}>
          <ul>
            {splitLines(asana.benifits).map((l, i) => (
              <li key={i}>{l}</li>
            ))}
          </ul>
        </Accordion>
      )}
      {asana.concequences && (
        <Accordion label="Precautions & Consequences" onToggle={onAccordionToggle}>
          <ul>
            {splitLines(asana.concequences).map((l, i) => (
              <li key={i}>{l}</li>
            ))}
          </ul>
        </Accordion>
      )}
    </DetailBox>
  );
};

// ─── Main Component ────────────────────────────────────────────────────────────

const Rogyog = () => {
  const [catIdx, setCatIdx] = useState(0);
  const [subcatIdx, setSubcatIdx] = useState(0);
  const [videoId, setVideoId] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [moreIndex, setMoreIndex] = useState(0);
  const [watchlist, toggleWatchlist] = useWatchlist();
  const playerRef = useRef(null);
  const moreSectionRef = useRef(null);

  const analytics = useRogyogAnalytics();

  const {
    query,
    activeTag,
    results,
    isSearching,
    setQuery,
    setActiveTag,
    clearSearch,
  } = useAsanaSearch();

  // ── Derived data ──
  const category = useMemo(() => Data?.[catIdx] ?? null, [catIdx]);
  const subcats = useMemo(() => category?.subcat ?? [], [category]);
  const subcat = useMemo(() => subcats[subcatIdx] ?? null, [subcats, subcatIdx]);
  const videos = useMemo(() => subcat?.subSubCat ?? [], [subcat]);
  const activeVideo = useMemo(
    () => videos.find((v) => v.asnVideoPath === videoId) ?? null,
    [videos, videoId],
  );

  // Videos to offer under "More Practices You May Like": the rest of the
  // current sub-category first, padded out with the category's other
  // sub-categories if that alone is thin.
  const morePractices = useMemo(() => {
    const pool = videos.filter((v) => v.asnVideoPath !== videoId);
    if (pool.length >= MORE_PAGE_SIZE) return pool;
    const seen = new Set(pool.map((v) => v.asnVideoPath));
    const extra = subcats
      .flatMap((s) => s.subSubCat ?? [])
      .filter((v) => v.asnVideoPath !== videoId && !seen.has(v.asnVideoPath));
    return [...pool, ...extra];
  }, [videos, subcats, videoId]);

  const visibleMore = morePractices.slice(moreIndex, moreIndex + MORE_PAGE_SIZE);
  const canMorePrev = moreIndex > 0;
  const canMoreNext = moreIndex + MORE_PAGE_SIZE < morePractices.length;

  // ── Page view on mount ──
  useEffect(() => {
    analytics.trackPageView("Rogyog — Yoga for Health", window.location.href);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Debounced search tracking ──
  useEffect(() => {
    if (query.trim() || activeTag) {
      analytics.trackSearchQuery(query, results.length);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, activeTag, results.length]);

  // ── Handlers ──
  const selectCat = useCallback(
    (i) => {
      const cat = Data?.[i];
      analytics.trackCategorySelect(cat?.category ?? "", i);
      setCatIdx(i);
      setSubcatIdx(0);
      setVideoId(null);
      setPlaying(false);
      setMoreIndex(0);
    },
    [analytics],
  );

  const selectSub = useCallback(
    (i) => {
      const sub = subcats[i];
      analytics.trackSubcategorySelect(sub?.subCatName ?? "", i, category?.category ?? "");
      setSubcatIdx(i);
      setVideoId(null);
      setPlaying(false);
      setMoreIndex(0);
    },
    [analytics, subcats, category],
  );

  const selectVid = useCallback(
    (id, source = "browse") => {
      const video =
        videos.find((v) => v.asnVideoPath === id) ?? results.find((r) => r.videoPath === id);
      analytics.trackVideoPlay(
        video ?? { asnVideoPath: id },
        category?.category ?? "",
        subcat?.subCatName ?? "",
        source,
      );
      setVideoId(id);
      // Explicit list/card clicks are already an intentional "play this"
      // action — only the initial auto-selected video waits for the
      // thumbnail's play button (see the `videos` effect below).
      setPlaying(true);
      setMoreIndex(0);
      clearSearch();
      setTimeout(
        () => playerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }),
        100,
      );
    },
    [analytics, videos, results, category, subcat, clearSearch],
  );

  const handleAccordionToggle = useCallback(
    (label, isOpen) => {
      analytics.trackAccordionToggle(label, isOpen, activeVideo, category?.category ?? "");
    },
    [analytics, activeVideo, category],
  );

  const handleFilterToggle = useCallback(() => {
    setFilterOpen((prev) => {
      const next = !prev;
      analytics.trackSearchPanelToggle(next);
      return next;
    });
  }, [analytics]);

  const handleClearSearch = useCallback(() => {
    analytics.trackSearchClear();
    clearSearch();
  }, [analytics, clearSearch]);

  const handleViewAllVideos = useCallback(() => {
    moreSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  const handleToggleWatchlist = useCallback(() => {
    if (!activeVideo) return;
    toggleWatchlist(activeVideo.asnVideoPath);
  }, [activeVideo, toggleWatchlist]);

  // Auto-select the first video of a (new) sub-category, but don't
  // autoplay it — matches the reference design's static "click to play"
  // thumbnail frame rather than audio starting unannounced on load.
  useEffect(() => {
    setVideoId(videos[0]?.asnVideoPath ?? null);
    setPlaying(false);
  }, [videos]);

  const isInWatchlist = activeVideo ? watchlist.has(activeVideo.asnVideoPath) : false;
  const conditionTags = (activeVideo?.asnTags ?? "")
    .split(",")
    .map((t) => t.trim())
    .filter(Boolean);

  return (
    <Section id="rogYog">
      <RogyogSeoMeta
        activeCategory={category?.category ?? ""}
        activeSubcat={subcat?.subCatName ?? ""}
        activeVideo={activeVideo}
        videos={videos}
      />

      <Inner>    

        {/* ── Hero ── */}
        <HeroWrap>
          <Eyebrow>
            <LeafIcon /> Yoga for Health
          </Eyebrow>
          <Title>Therapeutic Yoga for Your Well-being</Title>
          <Desc>
            Explore evidence-based yoga practices that support healing, balance and a healthier,
            happier you.
          </Desc>
        </HeroWrap>

        <BadgeRow>
          <BadgeCard>
            <BadgeIconWrap>
              <PersonIcon />
            </BadgeIconWrap>
            <BadgeLabel>Evidence-based Practices</BadgeLabel>
          </BadgeCard>
          <BadgeCard>
            <BadgeIconWrap>
              <LeafIcon />
            </BadgeIconWrap>
            <BadgeLabel>For All Ages &amp; Levels</BadgeLabel>
          </BadgeCard>
          <BadgeCard>
            <BadgeIconWrap>
              <HeartIcon />
            </BadgeIconWrap>
            <BadgeLabel>Mind, Body &amp; Breath</BadgeLabel>
          </BadgeCard>
          <BadgeCard>
            <BadgeIconWrap>
              <ShieldIcon />
            </BadgeIconWrap>
            <BadgeLabel>Safe &amp; Holistic Approach</BadgeLabel>
          </BadgeCard>
        </BadgeRow>

        {/* ── Category pills ── */}
        <CategoryPillsRow role="tablist" aria-label="Yoga categories">
          {Data?.map((cat, i) => {
            const Icon = CATEGORY_PILL_ICONS[i % CATEGORY_PILL_ICONS.length];
            const iconColor = CATEGORY_PILL_COLORS[i % CATEGORY_PILL_COLORS.length];
            return (
              <CategoryPill
                key={cat.sN}
                type="button"
                role="tab"
                aria-selected={i === catIdx}
                $active={i === catIdx}
                $iconColor={iconColor}
                onClick={() => selectCat(i)}
              >
                <Icon />
                {cat.category}
              </CategoryPill>
            );
          })}
        </CategoryPillsRow>

        {/* ── Search + Filter ── */}
        <SearchFilterRow>
          <SearchBox>
            <SearchIcon />
            <SearchInput
              type="search"
              placeholder="Search Asana by Disease / Condition"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              aria-label="Search asana by disease or condition"
              autoComplete="off"
              spellCheck="false"
            />
          </SearchBox>
          <FilterToggleButton
            type="button"
            $active={filterOpen}
            onClick={handleFilterToggle}
            aria-expanded={filterOpen}
            aria-controls="asana-filter-panel"
          >
            <FilterIcon /> Filter
          </FilterToggleButton>
        </SearchFilterRow>

        {filterOpen && (
          <FilterPanel id="asana-filter-panel" role="region" aria-label="Filter by condition">
            <FilterPanelTitle>Filter by condition</FilterPanelTitle>
            <TagSuggestions role="group">
              {ALL_TAGS.filter(Boolean).map((tag) => (
                <TagChip
                  key={tag}
                  type="button"
                  $active={activeTag === tag}
                  onClick={() => {
                    const isDeselect = activeTag === tag;
                    analytics.trackTagChipClick(tag, isDeselect);
                    setActiveTag(tag);
                  }}
                  aria-pressed={activeTag === tag}
                >
                  {tag}
                </TagChip>
              ))}
            </TagSuggestions>
          </FilterPanel>
        )}

        {isSearching && (
          <div style={{ maxWidth: 720, margin: "0 auto 26px" }}>
            <p style={{ fontSize: "0.85rem", color: "rgba(46,31,15,0.6)", marginBottom: 10 }}>
              {results.length} asana{results.length !== 1 ? "s" : ""} found
              {activeTag ? ` for "${activeTag}"` : query ? ` for "${query}"` : ""}
              {" · "}
              <button
                type="button"
                onClick={handleClearSearch}
                style={{
                  border: "none",
                  background: "none",
                  color: "#3d6b4f",
                  fontWeight: 700,
                  cursor: "pointer",
                  padding: 0,
                }}
              >
                Clear
              </button>
            </p>
            {results.length > 0 ? (
              <MoreGrid>
                {results.map((asana) => (
                  <MoreCard
                    key={asana.sN}
                    type="button"
                    onClick={() => {
                      analytics.trackSearchResultClick(asana, query, activeTag);
                      selectVid(asana.videoPath, "search");
                    }}
                    aria-label={`Play ${asana.name}`}
                  >
                    <MoreThumb>
                      <img
                        src={ytThumb(asana.videoPath)}
                        alt={asana.name}
                        loading="lazy"
                        onError={(e) => {
                          e.currentTarget.style.display = "none";
                        }}
                      />
                      {asana.duration && <MoreThumbDuration>{asana.duration}</MoreThumbDuration>}
                    </MoreThumb>
                    <MoreCardBody>
                      <MoreCardTitle>{asana.name}</MoreCardTitle>
                      <MoreCardSubtitle>
                        {asana.category} › {asana.subCatName}
                      </MoreCardSubtitle>
                    </MoreCardBody>
                  </MoreCard>
                ))}
              </MoreGrid>
            ) : (
              <NoResultsBox>
                <span className="icon">🧘</span>
                <p>
                  No asanas found.
                  <br />
                  Try a different condition or tag.
                </p>
              </NoResultsBox>
            )}
          </div>
        )}

        {/* ── Browse mode (hidden while search results are shown) ── */}
        {!isSearching && (
          <LayoutGrid>
            <Sidebar>
              <SideCard aria-label="Sub-categories">
                <SideCardHeader>Sub-categories</SideCardHeader>
                <SideList>
                  {subcats.length > 0 ? (
                    subcats.map((s, i) => (
                      <SideItem
                        key={s.sN}
                        type="button"
                        $active={i === subcatIdx}
                        onClick={() => selectSub(i)}
                        aria-pressed={i === subcatIdx}
                      >
                        <SideItemLabel>{s.subCatName}</SideItemLabel>
                        <SideBadge>{(s.subSubCat ?? []).length}</SideBadge>
                      </SideItem>
                    ))
                  ) : (
                    <SideItem as="div" style={{ opacity: 0.5, cursor: "default" }}>
                      <SideItemLabel>No sub-categories</SideItemLabel>
                    </SideItem>
                  )}
                </SideList>
              </SideCard>

              <SideCard aria-label="Videos">
                <SideCardHeader>Videos ({videos.length})</SideCardHeader>
                <SideList>
                  {videos.length > 0 ? (
                    videos.map((v) => (
                      <SideItem
                        key={v.sN}
                        type="button"
                        $active={v.asnVideoPath === videoId}
                        onClick={() => selectVid(v.asnVideoPath, "browse")}
                        aria-pressed={v.asnVideoPath === videoId}
                      >
                        <SideItemLabel>{v.subCatName}</SideItemLabel>
                        <SideBadge>{v.asnVideoDuration}</SideBadge>
                      </SideItem>
                    ))
                  ) : (
                    <SideItem as="div" style={{ opacity: 0.5, cursor: "default" }}>
                      <SideItemLabel>No videos</SideItemLabel>
                    </SideItem>
                  )}
                </SideList>
                {videos.length > 0 && (
                  <ViewAllButton type="button" onClick={handleViewAllVideos}>
                    View All Videos <ChevronRightIcon />
                  </ViewAllButton>
                )}
              </SideCard>

              <WatchlistCard
                type="button"
                onClick={handleToggleWatchlist}
                disabled={!activeVideo}
                aria-pressed={isInWatchlist}
              >
                <WatchlistIconWrap $active={isInWatchlist}>
                  <HeartIcon filled={isInWatchlist} />
                </WatchlistIconWrap>
                <WatchlistText>
                  <WatchlistTitle>
                    {isInWatchlist ? "Added to Watchlist" : "Add to Watchlist"}
                  </WatchlistTitle>
                  <WatchlistSubtitle>Save your favorite practices for later</WatchlistSubtitle>
                </WatchlistText>
                <WatchlistChevron>
                  <ChevronRightIcon />
                </WatchlistChevron>
              </WatchlistCard>
            </Sidebar>

            <MainCol>
              <PlayerCard ref={playerRef}>
                {videoId ? (
                  <>
                    <PlayerFrame>
                      {playing ? (
                        <iframe
                          src={`${YT_EMBED}/${videoId}?autoplay=1&rel=0&modestbranding=1`}
                          title={activeVideo?.subCatName ?? "Yoga video"}
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      ) : (
                        <PlayerThumbButton
                          type="button"
                          onClick={() => setPlaying(true)}
                          aria-label={`Play ${activeVideo?.subCatName ?? "video"}`}
                        >
                          <img
                            src={ytThumb(videoId)}
                            alt=""
                            onError={(e) => {
                              e.currentTarget.style.display = "none";
                            }}
                          />
                          <PlayerThumbPlayCircle>
                            <PlayIcon width="24" height="24" />
                          </PlayerThumbPlayCircle>
                        </PlayerThumbButton>
                      )}
                    </PlayerFrame>

                    <PlayerBody>
                      <PlayerTitleRow>
                        <PlayerTitle>{activeVideo?.subCatName}</PlayerTitle>
                        {activeVideo?.asnVideoDuration && (
                          <PlayerDuration>
                            <ClockIcon /> {activeVideo.asnVideoDuration}
                          </PlayerDuration>
                        )}
                      </PlayerTitleRow>
                      {conditionTags.length > 0 && (
                        <ConditionTagsRow>
                          {conditionTags.map((t) => (
                            <ConditionTag key={t}>{t}</ConditionTag>
                          ))}
                        </ConditionTagsRow>
                      )}
                    </PlayerBody>

                    <AsanaDetail asana={activeVideo} onAccordionToggle={handleAccordionToggle} />
                  </>
                ) : (
                  <EmptyPlayerState aria-live="polite">
                    <span className="icon">🧘</span>
                    <span>Select a video to begin your practice</span>
                  </EmptyPlayerState>
                )}
              </PlayerCard>

              {/* ── More Practices ── */}
              {morePractices.length > 0 && (
                <MoreSection ref={moreSectionRef}>
                  <MoreHeader>
                    <MoreTitle>More Practices You May Like</MoreTitle>
                    {morePractices.length > MORE_PAGE_SIZE && (
                      <ViewAllLink type="button" onClick={() => setMoreIndex(0)}>
                        View All
                      </ViewAllLink>
                    )}
                  </MoreHeader>
                  <MoreTrackWrap>
                    <MoreGrid>
                      {visibleMore.map((v, i) => {
                        const inWatchlist = watchlist.has(v.asnVideoPath);
                        return (
                          <MoreCard
                            key={v.sN}
                            as="div"
                            role="button"
                            tabIndex={0}
                            $index={i}
                            onClick={() => selectVid(v.asnVideoPath, "more_practices")}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                selectVid(v.asnVideoPath, "more_practices");
                              }
                            }}
                            aria-label={`Play ${v.subCatName}`}
                          >
                            <MoreThumb>
                              <img
                                src={ytThumb(v.asnVideoPath)}
                                alt=""
                                loading="lazy"
                                onError={(e) => {
                                  e.currentTarget.style.display = "none";
                                }}
                              />
                              {v.asnVideoDuration && (
                                <MoreThumbDuration>{v.asnVideoDuration}</MoreThumbDuration>
                              )}
                              <MoreBookmark
                                as="button"
                                type="button"
                                $active={inWatchlist}
                                aria-label={inWatchlist ? "Remove from watchlist" : "Add to watchlist"}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleWatchlist(v.asnVideoPath);
                                }}
                              >
                                <HeartIcon filled={inWatchlist} width="13" height="13" />
                              </MoreBookmark>
                            </MoreThumb>
                            <MoreCardBody>
                              <MoreCardTitle>{v.subCatName}</MoreCardTitle>
                              <MoreCardSubtitle>{subcat?.subCatName}</MoreCardSubtitle>
                            </MoreCardBody>
                          </MoreCard>
                        );
                      })}
                    </MoreGrid>
                    {morePractices.length > MORE_PAGE_SIZE && (
                      <CarouselNavButton
                        type="button"
                        onClick={() => setMoreIndex((i) => Math.min(i + MORE_PAGE_SIZE, morePractices.length - 1))}
                        disabled={!canMoreNext}
                        aria-label="Show more practices"
                      >
                        <ArrowRightIcon />
                      </CarouselNavButton>
                    )}
                  </MoreTrackWrap>
                </MoreSection>
              )}
            </MainCol>
          </LayoutGrid>
        )}

        {/* ── Bottom CTA ── */}
        <CTABanner>
          <CTALeft>
            <CTAIconWrap aria-hidden="true">🪷</CTAIconWrap>
            <div>
              <CTATitle>Start your journey to better health</CTATitle>
              <CTASubtitle>Small daily practices lead to big transformations.</CTASubtitle>
            </div>
          </CTALeft>
          <CTAButton
            href="#rogYog"
            onClick={(e) => {
              e.preventDefault();
              handleViewAllVideos();
            }}
          >
            Explore More Practices <ArrowRightIcon />
          </CTAButton>
        </CTABanner>
      </Inner>
    </Section>
  );
};

Rogyog.displayName = "Rogyog";
export default React.memo(Rogyog);